import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ParkingSpaceDto, ParkingSpaceService, AddImageToParkingSpaceRequest, ImageDto } from '../../../api/gen';
import { ResponsiveBaseComponent, JJamesAlertComponent, JjamesButtonComponent, JJamesImageContainerComponent, Role } from 'shared-lib';
import { ParkingZoneListComponent } from "../parking-zone-list/parking-zone-list.component";
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RoleService } from '../../auth/role-service';
import { areZonesHidden } from '../../auth/zone-visibility';
import { TranslatePipe } from '@ngx-translate/core';
import { QuickEasypassDialogComponent } from '../quick-easypass/quick-easypass-dialog.component';

@Component({
  selector: 'app-parking-space-item',
  standalone: true,
  imports: [ParkingZoneListComponent, JJamesAlertComponent, DatePipe, JjamesButtonComponent, JJamesImageContainerComponent, MatSnackBarModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './parking-space-item.component.html',
  styleUrl: './parking-space-item.component.scss'
})
export class ParkingSpaceItemComponent extends ResponsiveBaseComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private parkingSpaceService = inject(ParkingSpaceService);
  private snackBar = inject(MatSnackBar);
  private roleService = inject(RoleService);
  private dialog = inject(MatDialog);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Output() zoneDetails = new EventEmitter<string>();
  @Output() newZone = new EventEmitter<string>();
  @Output() imageUploaded = new EventEmitter<ImageDto>();
  @Output() parkingSpaceDeleted = new EventEmitter<string>();
  @Input() parkingSpace: ParkingSpaceDto = {} as ParkingSpaceDto;

  showValidationErrorsFor: string | null = null;
  isUploading = signal(false);
  isLoadingImages = signal(false);
  isDeleting = signal(false);
  spaceImages = signal<ImageDto[]>([]);
  isAdmin = signal(false);
  // Restricted operator member: sees the space name + Hotline only — no details, no zones.
  isRestrictedMember = signal(false);

  /** Zones of this site are shown to Administrators only; see {@link areZonesHidden}. */
  zonesHidden(): boolean {
    return areZonesHidden(this.parkingSpace?.id, this.isAdmin());
  }

  async ngOnInit(): Promise<void> {
    this.loadSpaceImages();
    const [isAdmin, isOperatorAdmin, isOperatorMember] = await Promise.all([
      this.roleService.checkAccess(Role.Administrator),
      this.roleService.checkAccess(Role.OperatorAdmin),
      this.roleService.checkAccess(Role.OperatorMember)
    ]);
    this.isAdmin.set(isAdmin);
    this.isRestrictedMember.set(isOperatorMember && !isOperatorAdmin && !isAdmin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parkingSpace'] && !changes['parkingSpace'].firstChange) {
      this.loadSpaceImages();
    }
  }

  private loadSpaceImages(): void {
    if (!this.parkingSpace?.id) {
      return;
    }

    this.isLoadingImages.set(true);
    this.parkingSpaceService.parkingSpaceParkingSpaceIdImagesGet(this.parkingSpace.id).subscribe({
      next: (images) => {
        this.spaceImages.set(images);
        this.isLoadingImages.set(false);
      },
      error: (error) => {
        console.error('Error loading parking space images:', error);
        this.spaceImages.set([]);
        this.isLoadingImages.set(false);
      }
    });
  }

  getSpaceImage(): string | undefined {
    const images = this.spaceImages();
    const primaryImage = images.find(img => img.isPrimary);
    if (primaryImage?.fileUrl) {
      return primaryImage.fileUrl;
    }
    return images[0]?.fileUrl ?? undefined;
  }

  onUploadClick(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !this.parkingSpace.id) {
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.snackBar.open('Image file size must be less than 5MB', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.uploadImage(file);
  }

  private uploadImage(file: File): void {
    this.isUploading.set(true);

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1];

      const request: AddImageToParkingSpaceRequest = {
        fileName: file.name,
        base64Data: base64Data,
        isPrimary: true
      };

      this.parkingSpaceService.parkingSpaceParkingSpaceIdImagesPost(this.parkingSpace.id!, request).subscribe({
        next: (imageDto) => {
          this.isUploading.set(false);
          this.snackBar.open('Image uploaded successfully', 'Close', {
            duration: 3000
          });
          this.spaceImages.update(images => [...images, imageDto]);
          this.imageUploaded.emit(imageDto);
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        },
        error: (error) => {
          this.isUploading.set(false);
          console.error('Error uploading image:', error);
          this.snackBar.open('Failed to upload image. Please try again.', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      });
    };

    reader.onerror = () => {
      this.isUploading.set(false);
      this.snackBar.open('Failed to read image file', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    };

    reader.readAsDataURL(file);
  }

  onInfoClick(event: Event, parkingSpaceId: string | undefined) {
    event.stopPropagation();
    if (parkingSpaceId) {
      this.showValidationErrorsFor = parkingSpaceId;
    }
  }
  viewParkingProcesses(): void {
    if (this.parkingSpace.id) {
      this.router.navigate(['/parking-space', this.parkingSpace.id, 'processes']);
    }
  }
  onNewZone(parkingAreaId: string | undefined) {
    if (parkingAreaId) {
      // Navigate to create parking zone route with parking area ID
      this.router.navigate(['/parking-zone/new', parkingAreaId]);
    }
  }
  onCloseAlert() {
    this.showValidationErrorsFor = null;
  }
  onZoneDetails(zoneId: string) {
    this.router.navigate(['/parking-zone', zoneId]);
  }

  onParkingSpaceDetails(parkingSpaceId: string | undefined) {
    if (parkingSpaceId) {
      this.router.navigate(['/parking-space', parkingSpaceId]);
    }
  }

  /**
   * The cashier's desk form. Offered on every space that has Quick Easypass switched on, including to
   * restricted members: they are the cashiers, and this button is the only thing besides Hotline they get.
   */
  onQuickEasypass(): void {
    this.dialog.open(QuickEasypassDialogComponent, {
      width: this.isHandset() ? '95vw' : '480px',
      maxWidth: '95vw',
      data: { parkingSpace: this.parkingSpace }
    });
  }

  onHotline(parkingSpaceId: string | undefined) {
    if (parkingSpaceId) {
      this.router.navigate(['/hotline', parkingSpaceId]);
    }
  }

  // Zone scrolling methods
  scrollZonesLeft(parkingAreaId: string): void {
    const container = document.getElementById(`zones-container-${parkingAreaId}`);
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  }

  scrollZonesRight(parkingAreaId: string): void {
    const container = document.getElementById(`zones-container-${parkingAreaId}`);
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }

  onDeleteParkingSpace(parkingSpaceId: string | undefined): void {
    if (!parkingSpaceId) {
      this.snackBar.open('Unable to delete: Parking space ID is missing', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Show confirmation dialog
    const confirmDelete = confirm(`Are you sure you want to delete "${this.parkingSpace.name || 'Unnamed Parking Space'}"? This action cannot be undone.`);
    if (!confirmDelete) {
      return;
    }

    this.isDeleting.set(true);
    this.parkingSpaceService.parkingSpaceParkingSpaceIdDelete(parkingSpaceId).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.snackBar.open('Parking space deleted successfully', 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.parkingSpaceDeleted.emit(parkingSpaceId);
      },
      error: (error) => {
        this.isDeleting.set(false);
        console.error('Error deleting parking space:', error);
        this.snackBar.open('Failed to delete parking space. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }
}
